import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, Menu, X, Sun, Moon } from 'lucide-react';
import { RESOURCE_LINKS, prefersReducedMotion } from './siteConfig';
import { MobileDrawer } from './MobileDrawer';

export const NAV_LINKS = [
  { label: 'Product', href: '/#capabilities', id: 'capabilities' },
  { label: 'How It Works', href: '/#how-it-works', id: 'how-it-works' },
  { label: 'Pricing', href: '/pricing' },
  { label: 'Methodology', href: '/methodology' },
];

export const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (!el) return false;
  el.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
  window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}#${id}`);
  return true;
};

export const ThemeToggle = ({ theme, onToggle, testId = 'theme-toggle' }) => (
  <button type="button" className="mk-icon-btn" onClick={onToggle} data-testid={testId}
    aria-label={theme === 'light' ? 'Switch to dark theme' : 'Switch to light theme'}>
    {theme === 'light' ? <Moon size={18} /> : <Sun size={18} />}
  </button>
);

const ResourcesMenu = () => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (e.type === 'keydown' ? e.key === 'Escape' : !ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', close);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', close); };
  }, [open]);
  return (
    <div className="mk-dropdown" ref={ref}>
      <button type="button" className="mk-nav-link" aria-expanded={open} aria-controls="mk-resources" onClick={() => setOpen((v) => !v)} data-testid="nav-resources">
        Resources <ChevronDown size={15} className={`mk-caret ${open ? 'open' : ''}`} aria-hidden="true" />
      </button>
      <ul id="mk-resources" className="mk-dropdown-menu" hidden={!open}>
        {RESOURCE_LINKS.map((r) => (
          <li key={r.href}><a href={r.href} data-testid={`resource-${r.href.slice(1)}`}>{r.label}</a></li>
        ))}
      </ul>
    </div>
  );
};

export const MarketingHeader = ({ isHome, onAuth, theme, onToggleTheme }) => {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const go = (e, link) => {
    setOpen(false);
    if (isHome && link.id && scrollToId(link.id)) e.preventDefault();
  };
  const auth = (e, mode) => { e.preventDefault(); setOpen(false); onAuth(mode); };

  return (
    <>
    <header className="mk-header" data-testid="site-header">
      <div className="mk-container mk-header-inner">
        <a href="/" className="mk-brand" data-testid="brand-home-link">
          <img src="/logo.png" alt="" width="30" height="30" />
          <span>TradeHive AI</span>
        </a>
        <nav className="mk-nav" aria-label="Primary">
          {NAV_LINKS.map((l) => (
            <a key={l.href} className="mk-nav-link" href={l.href} onClick={(e) => go(e, l)} data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, '-')}`}>{l.label}</a>
          ))}
          <ResourcesMenu />
        </nav>
        <div className="mk-actions">
          <ThemeToggle theme={theme} onToggle={onToggleTheme} />
          <a className="mk-btn mk-btn-ghost" href="/?auth=login" onClick={(e) => auth(e, 'login')} data-testid="nav-signin">Sign In</a>
          <a className="mk-btn mk-btn-primary" href="/?auth=signup" onClick={(e) => auth(e, 'signup')} data-testid="nav-start-trial">Start Free Trial</a>
        </div>
        <button type="button" className="mk-icon-btn mk-burger" aria-label={open ? 'Close menu' : 'Open menu'} aria-expanded={open}
          aria-controls="mk-drawer" onClick={() => setOpen((v) => !v)} data-testid="mobile-menu-toggle">
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>
    </header>
    {open && <MobileDrawer onNav={go} onAuth={auth} theme={theme} onToggleTheme={onToggleTheme} onClose={() => setOpen(false)} />}
    </>
  );
};
